/**
 * The sync error alert (PR 4b; Amanda: "Notification if something
 * doesn't sync - SYNC ERROR, BIG AND RED").
 *
 * Two forms over one rule:
 *   - PushFailedAlert, at the top of the Invoices page: every invoice the
 *     server's push_failed read names (latest attempt failed, never
 *     landed, not void; every month, every page), each linking to its
 *     preview.
 *   - InvoicePushFailedAlert, on one invoice's preview, from the push
 *     state the preview already reads.
 *
 * The two groups keep the batch bar's money distinction through
 * pushFailedKind (absenceIsKnown): "Did not send" only where Olive
 * watched nothing arrive, and "Sent, no answer" everywhere else, so a
 * copy that may already be in QuickBooks is never called safe to resend.
 * The batch bar names the second bucket "unconfirmed"; this alert does
 * not, because the Invoices page already uses "unconfirmed" for a total
 * nobody could compare.
 *
 * Display only: it blocks nothing and stops no push. It clears when a
 * later push lands, because the read it renders from no longer names the
 * invoice.
 *
 * Danger register: text-danger on pepper-soft (5.53:1, src/index.css),
 * role="alert", as VoidCopiesOpenBanner.
 */

import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import type { PushFailedRowWire } from '@/lib/api';
import { pushFailedKind, pushFailureSentence, type PushFailedKind } from '@/lib/qboPushStatus';
import { useSupportContact } from '@/hooks/useSupportContact';

const BOX = 'space-y-3 rounded-tight border-2 border-danger/60 bg-pepper-soft p-4';

export const PUSH_FAILED_GROUP_WORDS: Record<PushFailedKind, { heading: string; line: string }> = {
  not_sent: {
    heading: 'Did not send',
    line: "This didn't reach QuickBooks. Send it again.",
  },
  no_answer: {
    heading: 'Sent, no answer',
    line: "QuickBooks didn't answer, so this may be there already. Check QuickBooks before you send it again.",
  },
};

const GROUP_TESTID: Record<PushFailedKind, string> = {
  not_sent: 'qbo-push-failed-not-sent',
  no_answer: 'qbo-push-failed-no-answer',
};

function invoiceLabel(row: PushFailedRowWire): string {
  const doc = row.doc_number ?? '';
  const name = row.client_display_name;
  if (doc !== '' && name !== '') return `${name} (#${doc})`;
  if (doc !== '') return `Invoice #${doc}`;
  return name !== '' ? name : 'An invoice';
}

export interface PushFailedAlertProps {
  /** The server's uncapped push_failed_count. */
  count: number;
  /** The server's push_failed rows, newest failure first. */
  rows: readonly PushFailedRowWire[];
  tenantSlug: string;
}

export function PushFailedAlert({ count, rows, tenantSlug }: PushFailedAlertProps): ReactNode {
  const contact = useSupportContact();
  if (count <= 0 && rows.length === 0) return null;
  const groups: Record<PushFailedKind, PushFailedRowWire[]> = { not_sent: [], no_answer: [] };
  for (const r of rows) groups[pushFailedKind(r.qbo_last_error)].push(r);
  const more = count - rows.length;
  return (
    <section data-testid="qbo-push-failed-alert" role="alert" aria-labelledby="qbo-push-failed-heading" className={`mb-4 ${BOX}`}>
      <h2 id="qbo-push-failed-heading" className="font-display text-2xl font-semibold text-danger">
        {`QuickBooks sync error: ${count === 1 ? '1 invoice' : `${count} invoices`}`}
      </h2>
      {(['not_sent', 'no_answer'] as const).map((kind) =>
        groups[kind].length === 0 ? null : (
          <div key={kind} data-testid={GROUP_TESTID[kind]} className="space-y-1">
            <h3 className="text-base font-semibold text-danger">
              {`${PUSH_FAILED_GROUP_WORDS[kind].heading} (${groups[kind].length})`}
            </h3>
            <p className="text-sm text-ink">{PUSH_FAILED_GROUP_WORDS[kind].line}</p>
            <ul className="space-y-1">
              {groups[kind].map((r) => (
                <li key={r.id} data-testid={`qbo-push-failed-row-${r.id}`} className="text-sm text-ink">
                  <Link
                    to={`/t/${tenantSlug}/invoices/${r.id}`}
                    data-testid={`qbo-push-failed-link-${r.id}`}
                    className="font-semibold text-danger underline underline-offset-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sage-400"
                  >
                    {invoiceLabel(r)}
                  </Link>{' '}
                  <span className="text-ink-soft">{pushFailureSentence(r.qbo_last_error, contact)}</span>
                </li>
              ))}
            </ul>
          </div>
        ),
      )}
      {more > 0 && (
        <p data-testid="qbo-push-failed-more" className="text-sm text-ink">
          {`And ${more} more beyond these.`}
        </p>
      )}
    </section>
  );
}

export interface InvoicePushFailedAlertProps {
  /** The latest attempt's class token. */
  lastError: string | null | undefined;
}

/** The preview's form: one invoice, one group. The caller decides
 *  whether it shows, through pushFailedAlertShows. */
export function InvoicePushFailedAlert({ lastError }: InvoicePushFailedAlertProps): ReactNode {
  const contact = useSupportContact();
  const kind = pushFailedKind(lastError);
  const words = PUSH_FAILED_GROUP_WORDS[kind];
  return (
    <section data-testid="invoice-push-failed-alert" data-kind={kind} role="alert" aria-labelledby="invoice-push-failed-heading" className={BOX}>
      <h2 id="invoice-push-failed-heading" className="font-display text-xl font-semibold text-danger">
        {`QuickBooks sync error: ${words.heading.toLowerCase()}`}
      </h2>
      <p className="text-sm font-semibold text-ink">{words.line}</p>
      <p className="text-sm text-ink-soft">{pushFailureSentence(lastError, contact)}</p>
    </section>
  );
}
